import type { ComponentType, SVGProps } from "react";
import { Section } from "@/components/ds/Section/Section";
import { Container } from "@/components/ds/Container/Container";
import { Stack } from "@/components/ds/Stack/Stack";
import { Grid } from "@/components/ds/Grid/Grid";
import { Card } from "@/components/ds/Card/Card";
import { Text } from "@/components/ds/Text/Text";
import { Heading } from "@/components/ds/Heading/Heading";
import { Reveal } from "@/components/ds/Reveal/Reveal";
import { SmartphoneIcon } from "@/icons/SmartphoneIcon";
import { ClockIcon } from "@/icons/ClockIcon";
import { FileTextIcon } from "@/icons/FileTextIcon";
import styles from "./PainPoints.module.css";

interface PainPoint {
  readonly icon: ComponentType<SVGProps<SVGSVGElement>>;
  readonly title: string;
  readonly description: string;
}

const PAIN_POINTS: readonly PainPoint[] = [
  {
    icon: SmartphoneIcon,
    title: "Your rota still lives in a group chat",
    description:
      "Shift changes get buried. Cleaners miss them. Clients notice. And your evenings get eaten by WhatsApp admin.",
  },
  {
    icon: ClockIcon,
    title: "You hear it from the client first",
    description:
      "A cleaner's late, a shift gets missed, and the first call is from an angry customer. You can't be at every site, so you find out the hard way.",
  },
  {
    icon: FileTextIcon,
    title: "No proof of what actually happened",
    description:
      "A cleaner says they were there. Without a record, you can't back them up and you can't push back. When a client asks, you're empty-handed.",
  },
];

export function PainPoints() {
  return (
    <Section background="white">
      <Container>
        <Reveal>
          <Stack gap={10} align="center">
            <Stack gap={4} align="center">
              <Text
                as="span"
                size="xs"
                weight="semibold"
                color="emerald"
                className={styles.label}
              >
                SOUND FAMILIAR?
              </Text>
              <Heading level={2} size="h2">
                What running on WhatsApp actually costs you
              </Heading>
            </Stack>

            <Grid minChildWidth="260px" gap={6}>
              {PAIN_POINTS.map((point) => (
                <Card key={point.title} padding="lg" shadow>
                  <Stack gap={4}>
                    <span className={styles.iconBadge} aria-hidden>
                      <point.icon width={20} height={20} />
                    </span>
                    <Heading level={3} size="h4">
                      {point.title}
                    </Heading>
                    <Text color="primary">{point.description}</Text>
                  </Stack>
                </Card>
              ))}
            </Grid>
          </Stack>
        </Reveal>
      </Container>
    </Section>
  );
}
